'use client';

import { lazy, Suspense } from 'react';
import { usePathname } from 'next/navigation';
import { BootAuthResponse } from '@/lib/types';
import { ELoginDesignId } from '@/lib/types/boot';
import { useLoginInitializer } from '@/hooks/useLoginInitializer';
import { AuthBootProvider } from '../context/AuthBootContext';
import { initilizeUrlParams } from '@/lib/utils/urlParams';
import { useConsentContext } from '../context/ConsentContext';
import { initializeAuthServices } from '@/lib/utils/initializer';
import { getAuthBackgroundStyle } from '@/lib/utils/style';
import { useBackgroundProps } from '@/state/hooks/useBackgroundProps';
import { DefaultLoginLayout } from './layouts/default';
import type { ILoginLayoutProps } from './layouts/shared';

const StoreImpersonationLoginForm = lazy(() => import('./StoreImpersonationLoginForm'));
const BigFishCasinoLoginLayout = lazy(() =>
  import('./layouts/bigFishCasino').then(({ BigFishCasinoLoginLayout }) => ({
    default: BigFishCasinoLoginLayout,
  }))
);
const JackpotSlotsLoginLayout = lazy(() =>
  import('./layouts/jackpotSlots').then(({ JackpotSlotsLoginLayout }) => ({
    default: JackpotSlotsLoginLayout,
  }))
);

const LOGIN_LAYOUTS: Record<ELoginDesignId, React.FC<ILoginLayoutProps>> = {
  [ELoginDesignId.DEFAULT]: DefaultLoginLayout,
  [ELoginDesignId.PRODUCT_MADNESS]: DefaultLoginLayout,
  [ELoginDesignId.JACKPOT_SLOTS]: JackpotSlotsLoginLayout,
  [ELoginDesignId.BIG_FISH_CASINO]: BigFishCasinoLoginLayout,
};

interface ILoginClientWrapperProps {
  authBoot: BootAuthResponse;
  isWebview: boolean;
  sanitizedConsentHtml?: string;
  hideLoginChrome?: boolean;
  children: React.ReactNode;
}

export const LoginClientWrapper = ({
  authBoot,
  isWebview,
  sanitizedConsentHtml,
  hideLoginChrome = false,
  children,
}: ILoginClientWrapperProps): React.ReactElement => {
  // Client-only singleton init (per-tab, no concurrency issue).
  initializeAuthServices(authBoot);
  useLoginInitializer({ authBoot });
  initilizeUrlParams();
  const pathname = usePathname();
  const { storeTheme } = authBoot;
  const isImpersonationStore = authBoot.integration?.isImpersonationStore;
  const shouldHideLoginChrome = hideLoginChrome && !isImpersonationStore;

  // Skipped while the chrome is hidden so the loading cover keeps its own
  // background art instead of the login theme painting over it.
  useBackgroundProps(
    !shouldHideLoginChrome && storeTheme
      ? getAuthBackgroundStyle(authBoot, pathname)
      : null
  );
  const shouldShowLoginLayout =
    isImpersonationStore || (!hideLoginChrome && !storeTheme.login?.hideAllLoginMethods);

  const {
    hasConsent,
    shouldShowAlert,
    isAlertShaking,
    isConsentRequired,
    isConsentV2,
    handleConsentChange,
  } = useConsentContext();

  // Designs boot has not been taught yet fall back to the default chrome.
  const LoginLayout =
    LOGIN_LAYOUTS[storeTheme.general?.loginDesignId ?? ELoginDesignId.DEFAULT] ||
    DefaultLoginLayout;

  return (
    <AuthBootProvider authBoot={authBoot} isWebview={isWebview}>
      {shouldShowLoginLayout && (
        <Suspense fallback={null}>
          <LoginLayout
            fontFamily={storeTheme.login?.font || storeTheme.general?.font}
            hasConsent={hasConsent}
            isConsentRequired={isConsentRequired}
            shouldShowConsentAlert={shouldShowAlert}
            isConsentAlertShaking={isAlertShaking}
            isConsentV2={isConsentV2}
            sanitizedConsentHtml={sanitizedConsentHtml}
            onConsentChange={handleConsentChange}
          >
            {isImpersonationStore ? (
              <Suspense fallback={null}>
                <StoreImpersonationLoginForm />
              </Suspense>
            ) : (
              children
            )}
          </LoginLayout>
        </Suspense>
      )}
    </AuthBootProvider>
  );
};
